import { useEffect, useState } from 'react';
import { getN8nSettings, saveN8nSettings, testN8n } from '../../api/settings.api';

export default function SettingsAutomations({ onFlash }) {
  const getDefaultN8nUrl = () => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/n8n`;
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [stepSaved, setStepSaved] = useState(false);
  const [stepTested, setStepTested] = useState(false);
  const [form, setForm] = useState({
    n8n_url: getDefaultN8nUrl(),
    n8n_api_key: '',
    n8n_enabled: true,
    n8n_email_enabled: true,
    n8n_whatsapp_enabled: true
  });
  const [hasApiKey, setHasApiKey] = useState(false);
  const [showApiKeyInput, setShowApiKeyInput] = useState(true);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getN8nSettings();
      const apiKeyExists = !!data.hasApiKey;
      setForm(f => ({
        ...f,
        n8n_url: data.n8n_url || getDefaultN8nUrl(),
        n8n_enabled: data.n8n_enabled !== false,
        n8n_email_enabled: data.n8n_email_enabled !== false,
        n8n_whatsapp_enabled: data.n8n_whatsapp_enabled !== false,
        n8n_api_key: ''
      }));
      setHasApiKey(apiKeyExists);
      setShowApiKeyInput(!apiKeyExists);
    } catch (e) {
      onFlash?.('❌ Erreur chargement n8n');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.n8n_api_key) delete payload.n8n_api_key;
      const data = await saveN8nSettings(payload);
      const apiKeyExists = !!data.hasApiKey;
      setHasApiKey(apiKeyExists);
      setForm(f => ({ ...f, n8n_api_key: '' }));
      setShowApiKeyInput(!apiKeyExists);
      setStepSaved(true);
      onFlash?.('✅ N8N enregistré');
    } catch (e) {
      onFlash?.('❌ Erreur sauvegarde n8n');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      await testN8n();
      setStepTested(true);
      onFlash?.('✅ Connexion n8n OK');
    } catch (e) {
      onFlash?.('❌ Connexion n8n échouée');
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="p-4">Chargement…</div>;
  const stepFilled = !!form.n8n_url;
  const progressCount = [stepFilled, stepSaved, stepTested].filter(Boolean).length;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Automatisations</div>
            <div className="text-lg font-semibold text-slate-900">n8n par tenant</div>
            <div className="text-sm text-slate-600">URL + API Key isolées par client.</div>
          </div>
          <div className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
            Worker only
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">Guide client interactif</div>
            <div className="text-xs text-slate-800">n8n est centralise: l’app orchestre, n8n envoie.</div>
          </div>
          <button
            onClick={() => setShowGuide(v => !v)}
            className="text-xs px-3 py-1 rounded border border-slate-300 text-slate-900 bg-white"
          >
            {showGuide ? 'Masquer' : 'Afficher'}
          </button>
        </div>

        {showGuide && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className={`rounded-xl border p-3 ${stepFilled ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
              <div className="text-xs uppercase text-slate-500">Etape 1</div>
              <div className="text-sm font-semibold text-slate-900">URL n8n</div>
              <div className="text-xs text-slate-600">Ex: https://kirassgroup.online/n8n</div>
              <div className="mt-2 text-xs">
                Statut: {stepFilled ? 'OK' : 'A completer'}
              </div>
            </div>
            <div className={`rounded-xl border p-3 ${stepSaved ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
              <div className="text-xs uppercase text-slate-500">Etape 2</div>
              <div className="text-sm font-semibold text-slate-900">Enregistrer</div>
              <div className="text-xs text-slate-600">Sauvegarder la config.</div>
              <div className="mt-2 text-xs">
                Statut: {stepSaved ? 'OK' : 'En attente'}
              </div>
            </div>
            <div className={`rounded-xl border p-3 ${stepTested ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
              <div className="text-xs uppercase text-slate-500">Etape 3</div>
              <div className="text-sm font-semibold text-slate-900">Tester</div>
              <div className="text-xs text-slate-600">Verifier la connexion.</div>
              <div className="mt-2 text-xs">
                Statut: {stepTested ? 'OK' : 'En attente'}
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 text-xs text-slate-900">
          Progression: {progressCount}/3
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4">
            <div className="text-sm font-semibold text-slate-900">Connexion n8n</div>
            <div className="text-xs text-slate-500">La clé API reste chiffrée côté serveur.</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input className="px-3 py-2 border rounded pilot-input" placeholder="https://votre-domaine/n8n" value={form.n8n_url}
              onChange={e => setForm({ ...form, n8n_url: e.target.value })} />
            {showApiKeyInput ? (
              <input className="px-3 py-2 border rounded pilot-input" placeholder={hasApiKey ? 'API Key (déjà défini)' : 'API Key'} type="password" value={form.n8n_api_key}
                onChange={e => setForm({ ...form, n8n_api_key: e.target.value })} />
            ) : (
              <div className="flex items-center gap-3">
                <div className="text-sm text-slate-600">API Key masquée</div>
                <button
                  type="button"
                  className="text-xs px-3 py-1 rounded border border-slate-300 text-slate-900 bg-white"
                  onClick={() => setShowApiKeyInput(true)}
                >
                  Modifier la clé
                </button>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.n8n_enabled} onChange={e => setForm({ ...form, n8n_enabled: e.target.checked })} />
              Activer n8n
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.n8n_email_enabled} onChange={e => setForm({ ...form, n8n_email_enabled: e.target.checked })} />
              Canal Email
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.n8n_whatsapp_enabled} onChange={e => setForm({ ...form, n8n_whatsapp_enabled: e.target.checked })} />
              Canal WhatsApp
            </label>
          </div>
          <div className="mt-4 flex gap-3">
            <button onClick={save} disabled={saving} className="px-4 py-2 rounded bg-slate-900 text-white">
              {saving ? '...' : 'Enregistrer'}
            </button>
            <button onClick={runTest} disabled={testing} className="px-4 py-2 rounded bg-emerald-600 text-white">
              {testing ? '...' : 'Tester la connexion'}
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-900 p-5 text-white">
          <div className="text-sm font-semibold">Rappel sécurité</div>
          <div className="mt-2 text-xs text-slate-200">
            n8n exécute uniquement l’envoi. La cible et l’audit restent dans l’app.
          </div>
          <div className="mt-4 rounded-xl bg-white/10 p-3 text-xs">
            Activez ou désactivez chaque canal selon vos besoins.
          </div>
        </div>
      </div>
    </div>
  );
}
